import React, { useState, useEffect } from 'react';
import { Typography, List, Card, Button, Flex, Avatar, theme, Modal, Spin, message, Badge } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { accountService } from '../../features/account/services/accountService';
import { assistantService } from '../../features/assistant-management/services/assistantManagementService';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { filterAccountsByRole } from '../../features/account/utils/accountFilters';
import './AccountSelectionModal.css';

import { createLogger } from '../../utils/logger';
const logger = createLogger('Components-AccountSelectionModal');

const { Title, Paragraph, Text } = Typography;
const { useToken } = theme;

interface Account {
  id: string;
  companyName: string;
  status: string;
  type: 'buyer' | 'seller';
  email?: string;
}

interface AccountSelectionModalProps {
  visible: boolean;
  onCancel: () => void;
  onContinue: (accountId: string) => void;
  onCreateAccount?: () => void;
  initialSelectedAccountId?: string;
}

export const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  visible,
  onCancel,
  onContinue,
  onCreateAccount,
  initialSelectedAccountId,
}) => {
  const { token } = useToken();
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(initialSelectedAccountId || null);
  const [agentsCounts, setAgentsCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (visible) {
      loadAccounts();
      // Set selected account when modal opens
      if (initialSelectedAccountId) {
        setSelectedAccountId(initialSelectedAccountId);
      }
    }
  }, [visible, initialSelectedAccountId]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await accountService.getAccounts();
      logger.info('Accounts response:', response);

      if (response && response.data && Array.isArray(response.data)) {
        // Filter by role and allowedAccountIds first
        const roleFilteredAccounts = filterAccountsByRole(response.data, user);

        // Then filter only active accounts
        const activeAccounts = roleFilteredAccounts.filter(
          (account: Account) => account.status === 'active'
        );
        setAccounts(activeAccounts);

        // Load agents count for each account
        const countsPromises = activeAccounts.map(async (account: Account) => {
          try {
            const count = await assistantService.getAssistantsCount(account.id);
            return { accountId: account.id, count };
          } catch (error) {
            logger.error(`Error getting agents count for account ${account.id}:`, error);
            return { accountId: account.id, count: 0 };
          }
        });

        const counts = await Promise.all(countsPromises);
        const countsMap = counts.reduce((acc, { accountId, count }) => {
          acc[accountId] = count;
          return acc;
        }, {} as Record<string, number>);

        setAgentsCounts(countsMap);
      } else {
        logger.error('Invalid response format:', response);
        setAccounts([]);
      }
    } catch (error) {
      logger.error('Error loading accounts:', error);
      message.error('Failed to load accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountId(accountId);
    onContinue(accountId);
  };

  const handleContinue = () => {
    if (!selectedAccountId) {
      message.warning('Please select an account');
      return;
    }
    onContinue(selectedAccountId);
  };

  const handleCancel = () => {
    setSelectedAccountId(null);
    onCancel();
  };

  const getInitials = (name: string): string => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      width={500}
      className="account-selection-modal"
    >
      <Flex vertical gap={token.padding}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Build a new Virtual Agent
        </Title>
        <Paragraph type="secondary">
          Configure an AI-powered assistant for any client account. Just select where it belongs and start customizing.
        </Paragraph>

        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: 200 }}>
            <Spin size="large" />
          </Flex>
        ) : accounts.length > 0 ? (
          <>
            <div className="accounts-list-container">
              <List
                dataSource={accounts}
                renderItem={(account) => (
                  <List.Item
                    className={`account-list-item ${selectedAccountId === account.id ? 'selected' : ''}`}
                    onClick={() => handleAccountSelect(account.id)}
                    style={{ cursor: 'pointer', padding: 0, margin:  0 }}
                  >
                    <Flex 
                      align="center" 
                      className="account-item-content"
                      style={{ 
                        width: '100%', 
                        border: selectedAccountId === account.id 
                          ? '2px solid var(--ant-colorPrimary)' 
                          : '1px solid #21212F15', 
                        padding: '24px', 
                        borderRadius: '8px', 
                      }}
                    >
                      <Avatar
                        size="large"
                        className="avatar-icon"
                        style={{
                          background: 'var(--ant-colorAvatar)',
                          color: 'var(--ant-colorIcon)',
                          fontWeight: 600,
                          marginRight: token.padding,
                          borderRadius: 'var(--ant-borderRadiusXS)',
                        }}
                      >
                        {getInitials(account.companyName)}
                      </Avatar>
                      <Flex vertical style={{ flex: 1 }}>
                        <Text strong>{account.companyName}</Text>
                        <Text type="secondary">{agentsCounts[account.id] || 0} agents</Text>
                      </Flex>
                      <Badge
                        status={account.status === 'active' ? 'success' : 'default'}
                        text={account.status === 'active' ? 'Active' : 'Inactive'}
                      />
                    </Flex>
                  </List.Item>
                )}
              />
            </div>

            {onCreateAccount && (
              <Card
                style={{
                  background: '#eff0eb',
                  border: '1px solid rgba(33, 33, 47, 0.06)',
                  borderRadius: '8px',
                }}
                bodyStyle={{
                  padding: 0,
                }}
              >
                <Flex vertical align="center" gap={8} style={{ padding: '12px 24px', margin: '0' }}>
                  <Flex vertical align="center" gap={4} >
                    <Text
                      strong
                      style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        lineHeight: '28px',
                        color: 'rgba(33, 33, 47, 0.88)',
                        textAlign: 'center',
                      }}
                    >
                      Can't find the right account?
                    </Text>
                  
                  </Flex>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={onCreateAccount}
                    style={{
                      height: '32px',
                      padding: '0 16px',
                      borderRadius: '12px',
                      color: 'var(--ant-colorSecondary)',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '22px',
                    }}
                  >
                    Create a new account
                  </Button>
                </Flex>
              </Card>
            )}
          </>
        ) : (
          <Card style={{ background: token.colorFillAlter }}>
            <Flex vertical align="center" gap={16}>
              <Text type="secondary">No active accounts found</Text>
              {onCreateAccount && (
                <Button type="primary" icon={<PlusOutlined />} onClick={onCreateAccount}>
                  Create a new account
                </Button>
              )}
            </Flex>
          </Card>
        )}

        <Flex justify="flex-end" gap={token.padding}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleContinue} disabled={!selectedAccountId}>
            Continue
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
